import { GeoStatus, Icon } from '@carrent/ui'

/**
 * Трек поездки в консоли.
 *
 * ── Почему пояснение стоит выше данных ────────────────────────────
 *
 * Диспетчер видит три точки за четырёхчасовую поездку и делает вывод
 * о водителе. Но браузер перестаёт определять место, когда экран
 * гаснет или приложение уходит в фон, а водитель за рулём не держит
 * приложение открытым. Разрывы — поведение платформы, а не поведение
 * человека.
 *
 * Поэтому пояснение идёт первым, до цифр: прочитанное после вывода
 * его уже не изменит.
 *
 * ── Почему нет процента покрытия ──────────────────────────────────
 *
 * Любая единая цифра «полноты» читалась бы как оценка водителя,
 * а оценивать по этим данным нельзя. Показываются факты: сколько
 * точек, в каких границах, где заметные паузы, чем снято.
 */

function formatTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })
}

function formatGap(minutes) {
  if (minutes < 60) return `${minutes} мин`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h} ч ${m} мин` : `${h} ч`
}

/** Точка акта: где машину взяли или куда пригнали. */
export function ActPoint({ label, location }) {
  if (!location) {
    return (
      <div className="act-point">
        <p className="act-point__label">{label}</p>
        <GeoStatus source="none" compact />
      </div>
    )
  }

  const coords = `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`

  return (
    <div className="act-point">
      <p className="act-point__label">{label}</p>
      <a
        className="act-point__coords"
        href={`https://www.openstreetmap.org/?mlat=${location.lat}&mlon=${location.lng}#map=17/${location.lat}/${location.lng}`}
        target="_blank"
        rel="noreferrer"
      >
        <Icon name="map" size={13} />
        {coords}
      </a>
      <p className="act-point__meta">
        {location.source_label}
        {location.accuracy != null && ` · ±${Math.round(location.accuracy)} м`}
      </p>
    </div>
  )
}

export default function TripTrack({ track, startLocation, endLocation }) {
  return (
    <section className="track">
      <div className="track__points">
        <ActPoint label="Выдача" location={startLocation} />
        <ActPoint label="Возврат" location={endLocation} />
      </div>

      {/* Пояснение выше цифр: прочитанное после вывода его не изменит. */}
      <p className="track__note">
        <Icon name="file" size={14} />
        {track?.note}
      </p>

      {!track?.points ? (
        <p className="track__empty">
          Точек нет. Приложение не определяло место — например, телефон был
          заблокирован всю поездку.
        </p>
      ) : (
        <>
          <dl className="track__facts">
            <div>
              <dt>Точек</dt>
              <dd>{track.points}</dd>
            </div>
            <div>
              <dt>Первая</dt>
              <dd>{formatTime(track.first_at)}</dd>
            </div>
            <div>
              <dt>Последняя</dt>
              <dd>{formatTime(track.last_at)}</dd>
            </div>
          </dl>

          {Object.keys(track.sources || {}).length > 0 && (
            <p className="track__sources">
              Чем снято:{' '}
              {Object.entries(track.sources)
                .map(([source, count]) => `${source} — ${count}`)
                .join(' · ')}
            </p>
          )}

          {track.gaps?.length > 0 && (
            <div className="track__gaps">
              <p className="track__gaps-title">
                Паузы в записи ({track.gaps.length})
              </p>
              <ul>
                {track.gaps.map((gap) => (
                  <li key={gap.from}>
                    {formatTime(gap.from)} → {formatTime(gap.to)}
                    <span className="track__gap-length">{formatGap(gap.minutes)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  )
}
